import { API } from '@/api'
import { DEFAULT_UPLOAD_CONCURRENCY, TOKEN_CACHE_DURATION } from '@/consts/upload'
import { ConcurrentPool, TaskResult } from '@/utils/concurrent-pool'
import http from '@/utils/http'
import { generateMD5HashWithChunks } from '@/utils/md5'
import { BaseChunkUploader, UploadPausedError } from '@/utils/upload'

import {
  ImageRegisterConfig,
  ImageRegistrationResult,
  RequestOptions,
  TokenProvider,
  UploadChunkResponse,
  UploadContext,
} from './types'

export const CHUNK_SIZE = 2 * 1024 * 1024 // 2MB

const DEFAULT_OPTIONS = {
  chunkSize: CHUNK_SIZE,
  concurrency: DEFAULT_UPLOAD_CONCURRENCY,
}

const UPLOAD_CONSTANTS = {
  PRIVILEGE_TYPE: 56, // Privilege_ImageStore
  ERROR_CODE_ALREADY_REGISTERED: 174,
  CHECKSUM_ALGORITHM: '1',
  AUTH_ERROR_RETRY_DELAY: 1000, // 1s delay for auth errors
  INITIAL_RETRY_DELAY: 2000, // 2s initial delay for exponential backoff
  MAX_RETRY_DELAY: 60000, // 60s maximum delay
} as const

/**
 * Calculate retry delay using exponential backoff
 * delay = min(INITIAL_RETRY_DELAY * 2^attempt, MAX_RETRY_DELAY)
 * @param attempt The current retry attempt (0-indexed)
 * @returns Delay in milliseconds
 */
function calculateRetryDelay(attempt: number): number {
  const delay = UPLOAD_CONSTANTS.INITIAL_RETRY_DELAY * Math.pow(2, attempt)
  return Math.min(delay, UPLOAD_CONSTANTS.MAX_RETRY_DELAY)
}

/**
 * Creates a cached token provider for image uploads
 * Token has 15-min validity from server, cache refreshes after TOKEN_CACHE_DURATION
 * Includes invalidate() method to force refresh on next call
 */
function createImageTokenProvider(imageId: number): TokenProvider {
  let cachedToken: string | null = null
  let cacheExpiry = 0

  const getToken = async (): Promise<string> => {
    if (cachedToken && Date.now() < cacheExpiry) {
      return cachedToken
    }

    const response = await API.generateImageUploadToken({
      source_id: imageId,
      privilege_type: UPLOAD_CONSTANTS.PRIVILEGE_TYPE,
    })

    if (response.code !== 0 || !response.token) {
      throw new Error(
        `Token generation failed with code ${response.code}: ${response.msg || 'Unknown error'}`,
      )
    }

    cachedToken = response.token
    cacheExpiry = Date.now() + TOKEN_CACHE_DURATION
    return cachedToken
  }

  // Expose invalidate method to force token refresh
  getToken.invalidate = () => {
    cachedToken = null
    cacheExpiry = 0
  }

  return getToken
}

export interface IImageUploaderClientOptions {
  chunkSize: number
  concurrency?: number
  requestOptions?: RequestOptions
}

export interface UploadProgress {
  loaded: number
  total: number
  percentage: number
  chunkIndex: number
}

interface PausedUploadState {
  file: File
  imageName?: string
  description?: string
  chunkList: Blob[]
  uploadContext: UploadContext
  currentIndex: number
  isResumeUpload: boolean
}

export class ImageUploaderClient extends BaseChunkUploader {
  private imageUploaderClientOptions: IImageUploaderClientOptions
  private progressCallback?: (progress: UploadProgress) => void
  private pausedState?: PausedUploadState
  private requestOptions!: RequestOptions

  constructor(options: IImageUploaderClientOptions) {
    super({
      chunkSize: options.chunkSize ?? CHUNK_SIZE,
      concurrency: options.concurrency ?? DEFAULT_UPLOAD_CONCURRENCY,
    })
    this.imageUploaderClientOptions = Object.assign({}, DEFAULT_OPTIONS, options)
  }

  /**
   * Split file object into chunks and calculate MD5 based on chunks
   * Override to use generateMD5HashWithChunks instead of base class implementation
   * @param file The file to upload
   * @returns Returns MD5 and chunk list
   */
  public override async getChunkListAndFileMd5(
    file: File,
  ): Promise<{ md5: string; chunkList: Blob[] }> {
    return await generateMD5HashWithChunks(file, this.imageUploaderClientOptions.chunkSize)
  }

  /**
   * Register image only
   * @param file The file to register
   * @param imageName Image name
   * @param description Image description
   * @returns Registration result with upload context
   */
  public async registerImage(
    file: File,
    imageName: string,
    description: string,
    format?: number,
    config?: ImageRegisterConfig,
  ): Promise<ImageRegistrationResult> {
    this.validateRequestOptions()

    const { md5, chunkList } = await this.getChunkListAndFileMd5(file)
    const uploadContext = await this.initializeUpload(
      file,
      imageName,
      description,
      md5,
      format,
      config,
    )

    return {
      ...uploadContext,
      md5,
      chunkList,
    }
  }

  /**
   * Upload image chunks only
   * @param registrationResult Result from registerImage
   * @param file The file to upload
   * @param onProgress Progress callback function
   * @returns Upload result
   */
  public async uploadImageChunks(
    registrationResult: ImageRegistrationResult,
    file: File,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<any> {
    this.progressCallback = onProgress

    const fullContext = {
      imageId: registrationResult.imageId,
      storeServiceUrl: registrationResult.storeServiceUrl,
      getUploadToken: registrationResult.getUploadToken,
      md5: registrationResult.md5,
      fileName: file.name,
      fileSize: file.size,
    }

    if (this.isCanceled()) throw new Error('canceled')

    try {
      await this.uploadChunks(registrationResult.chunkList, fullContext)

      return await this.requestOptions.finishImagePartUploadFunc({
        imageId: registrationResult.imageId,
        fileMd5: registrationResult.md5,
      })
    } catch (error) {
      if (error instanceof UploadPausedError) {
        // Save state for resume
        this.pausedState = {
          file,
          chunkList: registrationResult.chunkList,
          uploadContext: fullContext,
          currentIndex: 0, // Will be updated by upload loops
          isResumeUpload: true,
        }
      }
      throw error
    }
  }

  /**
   * Image upload method (combines registration and upload)
   * @param file The file to upload
   * @param imageName Image name
   * @param description Image description
   * @param onProgress Progress callback function
   * @param onImageRegistered Callback when image is registered
   * @returns Upload result
   */
  public async uploadImage(
    file: File,
    imageName: string,
    description: string,
    format?: number,
    onProgress?: (progress: UploadProgress) => void,
    onImageRegistered?: (registrationResult: ImageRegistrationResult) => void,
    config?: ImageRegisterConfig,
  ): Promise<any> {
    // Step 1: Register image
    const registrationResult = await this.registerImage(
      file,
      imageName,
      description,
      format,
      config,
    )

    // Notify that image has been registered
    if (onImageRegistered) {
      onImageRegistered(registrationResult)
    }

    // Step 2: Upload chunks
    return await this.uploadImageChunks(registrationResult, file, onProgress)
  }

  /**
   * Continue upload for existing registered image
   * @param file The file to upload
   * @param imageId Existing image ID from server
   * @param storeServiceUrl Store service URL from pending image data
   * @param onProgress Progress callback function
   * @returns Upload result
   */
  public async continueUpload(
    file: File,
    imageId: number,
    storeServiceUrl: string,
    onProgress?: (progress: UploadProgress) => void,
  ): Promise<any> {
    // Reset state for fresh upload
    this.resetState()

    this.progressCallback = onProgress
    this.validateRequestOptions()

    const { md5, chunkList } = await this.getChunkListAndFileMd5(file)

    // Skip image registration, use existing imageId with token provider
    const getUploadToken = createImageTokenProvider(imageId)

    if (this.isCanceled()) throw new Error('canceled')

    const fullContext = {
      imageId,
      storeServiceUrl,
      getUploadToken,
      md5,
      fileName: file.name,
      fileSize: file.size,
    }

    try {
      await this.uploadChunksWithResume(chunkList, fullContext)

      return await this.requestOptions.finishImagePartUploadFunc({
        imageId,
        fileMd5: md5,
      })
    } catch (error) {
      if (error instanceof UploadPausedError) {
        // Save state for resume
        this.pausedState = {
          file,
          chunkList,
          uploadContext: fullContext,
          currentIndex: 0, // Will be updated by upload loops
          isResumeUpload: true,
        }
      }
      throw error
    }
  }

  private validateRequestOptions() {
    const requestOptions = this.imageUploaderClientOptions.requestOptions
    if (
      !requestOptions?.initImagePartUploadFunc ||
      !requestOptions?.uploadPartImageFunc ||
      !requestOptions?.finishImagePartUploadFunc
    ) {
      throw Error(
        'invalid request options, need initImagePartUploadFunc, uploadPartImageFunc and finishImagePartUploadFunc',
      )
    }
    this.requestOptions = requestOptions
  }

  private async initializeUpload(
    file: File,
    imageName: string,
    description: string,
    md5: string,
    format?: number,
    config?: ImageRegisterConfig,
  ) {
    return await this.requestOptions.initImagePartUploadFunc({
      file,
      imageName,
      description,
      fileMd5: md5,
      format,
      config,
    })
  }

  private async uploadChunks(chunkList: Blob[], uploadContext: UploadContext): Promise<void> {
    // For single chunk files, just upload directly
    if (chunkList.length === 1) {
      if (this.isCanceled()) throw new Error('canceled')
      if (this.isPaused()) throw new UploadPausedError()
      const response = await this.uploadSingleChunk(chunkList[0], 0, uploadContext)

      // Verify single chunk was stored
      const blkWritten = response?.info?.blkWritten || {}
      if (blkWritten['0'] === false) {
        throw new Error('Failed to upload chunk [0] - server did not store it')
      }

      // Single chunk verified, show 100%
      this.updateProgressComplete(uploadContext.fileSize)
      return
    }

    // Step 1: Upload chunk 0 first to probe blkWritten
    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) throw new UploadPausedError()
    await this.uploadSingleChunk(chunkList[0], 0, uploadContext)

    let completedCount = 1
    this.updateProgressWithIndex(completedCount, chunkList.length, uploadContext.fileSize, 0)

    // Step 2: Upload remaining chunks in parallel
    const pool = new ConcurrentPool<UploadChunkResponse>({
      concurrency: this.concurrency,
      shouldAbort: () => this.shouldAbort(),
      onTaskComplete: () => {
        completedCount++
        this.updateProgressWithIndex(
          completedCount,
          chunkList.length,
          uploadContext.fileSize,
          completedCount - 1,
        )
      },
    })

    const tasks = []
    for (let i = 1; i < chunkList.length; i++) {
      tasks.push({
        index: i,
        task: () => this.uploadSingleChunk(chunkList[i], i, uploadContext),
      })
    }

    const results = await pool.execute(tasks)

    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) throw new UploadPausedError()

    // Check for task execution failures
    const taskFailed = results.filter((r: TaskResult<UploadChunkResponse>) => !r.success)
    if (taskFailed.length > 0) {
      const failedIndices = taskFailed
        .map((f: TaskResult<UploadChunkResponse>) => f.index)
        .join(', ')
      throw new Error(`Failed to upload chunks [${failedIndices}]`)
    }

    // Step 3: Verify blkWritten and retry failed chunks
    await this.verifyAndRetryFailedImageChunks(chunkList, uploadContext, results, pool)

    // All chunks verified, show 100%
    this.updateProgressComplete(uploadContext.fileSize)
  }

  private async uploadChunksWithResume(
    chunkList: Blob[],
    uploadContext: UploadContext,
  ): Promise<void> {
    // For single chunk files, just upload directly
    if (chunkList.length === 1) {
      if (this.isCanceled()) throw new Error('canceled')
      if (this.isPaused()) throw new UploadPausedError()
      const response = await this.uploadSingleChunk(chunkList[0], 0, uploadContext)

      // Verify single chunk was stored
      const blkWritten = response?.info?.blkWritten || {}
      if (blkWritten['0'] === false) {
        throw new Error('Failed to upload chunk [0] - server did not store it')
      }

      // Single chunk verified, show 100%
      this.updateProgressComplete(uploadContext.fileSize)
      return
    }

    // Step 1: Upload chunk 0 to probe blkWritten
    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) throw new UploadPausedError()
    const firstResponse = await this.uploadSingleChunk(chunkList[0], 0, uploadContext)

    const blkWritten = firstResponse?.info?.blkWritten || {}
    const skippedCount = Object.keys(blkWritten).filter((k) => blkWritten[k]).length

    // Step 2: Filter chunks that need uploading (skip already uploaded)
    const chunksToUpload: number[] = []
    for (let i = 1; i < chunkList.length; i++) {
      if (!blkWritten[i.toString()]) {
        chunksToUpload.push(i)
      }
    }

    // Count initial progress (chunk 0 + already uploaded chunks)
    let completedCount = skippedCount > 0 ? skippedCount : 1
    this.updateProgressWithIndex(completedCount, chunkList.length, uploadContext.fileSize, 0)

    // If all chunks already uploaded, we're done
    if (chunksToUpload.length === 0) {
      this.updateProgressWithIndex(
        chunkList.length,
        chunkList.length,
        uploadContext.fileSize,
        chunkList.length - 1,
      )
      return
    }

    // Step 3: Upload remaining chunks in parallel
    const pool = new ConcurrentPool<UploadChunkResponse>({
      concurrency: this.concurrency,
      shouldAbort: () => this.shouldAbort(),
      onTaskComplete: () => {
        completedCount++
        this.updateProgressWithIndex(
          completedCount,
          chunkList.length,
          uploadContext.fileSize,
          completedCount - 1,
        )
      },
    })

    const tasks = chunksToUpload.map((i) => ({
      index: i,
      task: () => this.uploadSingleChunk(chunkList[i], i, uploadContext),
    }))

    const results = await pool.execute(tasks)

    if (this.isCanceled()) throw new Error('canceled')
    if (this.isPaused()) throw new UploadPausedError()

    // Check for task execution failures
    const taskFailed = results.filter((r: TaskResult<UploadChunkResponse>) => !r.success)
    if (taskFailed.length > 0) {
      const failedIndices = taskFailed
        .map((f: TaskResult<UploadChunkResponse>) => f.index)
        .join(', ')
      throw new Error(`Failed to upload chunks [${failedIndices}]`)
    }

    // Step 4: Verify blkWritten and retry failed chunks
    await this.verifyAndRetryFailedImageChunks(chunkList, uploadContext, results, pool)

    // All chunks verified, show 100%
    this.updateProgressComplete(uploadContext.fileSize)
  }

  /**
   * Upload a single chunk with infinite retry logic and auth error handling
   * Custom implementation for image uploads with token refresh support
   * Retries indefinitely until success, pause, or cancel
   */
  private async uploadSingleChunk(chunk: Blob, index: number, context: UploadContext) {
    let attempt = 0

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // Check pause/cancel before each attempt
      if (this.isPaused()) throw new UploadPausedError()
      if (this.isCanceled()) throw new Error('canceled')

      // Create AbortController for this request
      const abortController = new AbortController()
      this.activeAbortControllers.add(abortController)

      try {
        const result = await this.requestOptions.uploadPartImageFunc({
          chunk,
          index,
          fileMd5: context.md5,
          fileName: context.fileName,
          fileSize: context.fileSize,
          imageId: context.imageId,
          storeServiceUrl: context.storeServiceUrl,
          getUploadToken: context.getUploadToken,
          signal: abortController.signal,
        })
        this.activeAbortControllers.delete(abortController)
        return result
      } catch (error: any) {
        this.activeAbortControllers.delete(abortController)

        // Handle abort (from pause or cancel)
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          if (this.isPaused()) throw new UploadPausedError()
          if (this.isCanceled()) throw new Error('canceled')
        }

        if (this.isCanceled()) throw new Error('canceled')
        if (this.isPaused()) throw new UploadPausedError()

        // Check if this is an auth error (token expired)
        const isAuthError =
          error?.code === 176 ||
          error?.code === 401 ||
          error?.message?.includes('401') ||
          error?.message?.includes('token')

        if (isAuthError) {
          // Auth error - invalidate token cache and retry with fresh token
          context.getUploadToken.invalidate?.()
          console.log(`Upload chunk ${index} auth failed, retrying with fresh token...`)
          await this.delay(UPLOAD_CONSTANTS.AUTH_ERROR_RETRY_DELAY)
          continue
        }

        // Log retry attempt and wait before retrying with exponential backoff
        const retryDelay = calculateRetryDelay(attempt)
        console.log(
          `Upload chunk ${index} failed (attempt ${attempt + 1}), retrying in ${retryDelay / 1000}s...`,
        )
        await this.delay(retryDelay)
        attempt++
      }
    }
  }

  private updateProgressWithIndex(
    completedChunks: number,
    totalChunks: number,
    fileSize: number,
    currentIndex: number,
  ) {
    if (this.progressCallback) {
      // Cap at 99% - 100% is reserved for after verification completes
      const percentage = Math.min(Math.round((completedChunks / totalChunks) * 100), 99)
      this.progressCallback({
        loaded: Math.min(completedChunks * this.imageUploaderClientOptions.chunkSize, fileSize),
        total: fileSize,
        percentage,
        chunkIndex: currentIndex,
      })
    }
  }

  /**
   * Update progress to 100% after all chunks verified
   */
  private updateProgressComplete(fileSize: number) {
    if (this.progressCallback) {
      this.progressCallback({
        loaded: fileSize,
        total: fileSize,
        percentage: 100,
        chunkIndex: -1,
      })
    }
  }

  /**
   * Verify blkWritten status and retry chunks that weren't stored
   * Server may return code: 0 but blkWritten[i] = false for some chunks
   * Retries indefinitely until all chunks pass or upload is paused/canceled
   */
  private async verifyAndRetryFailedImageChunks(
    chunkList: Blob[],
    uploadContext: UploadContext,
    results: TaskResult<UploadChunkResponse>[],
    pool: ConcurrentPool<UploadChunkResponse>,
  ): Promise<void> {
    // Get the latest blkWritten from the last successful response
    const successfulResults = results.filter((r) => r.success && r.result?.info?.blkWritten)
    if (successfulResults.length === 0) {
      return // No blkWritten info available, assume all chunks were stored
    }

    // Use the blkWritten from any successful response (they should all have the same info)
    const lastResult = successfulResults[successfulResults.length - 1]
    let blkWritten = lastResult.result?.info?.blkWritten || {}

    let failedIndices = this.getFailedChunkIndices(blkWritten, chunkList.length)
    if (failedIndices.length === 0) {
      return
    }

    let retry = 0
    while (failedIndices.length > 0) {
      retry++
      console.log(
        `[blkWritten verification] Retrying ${failedIndices.length} failed chunks (attempt ${retry})`,
      )

      if (this.isCanceled()) throw new Error('canceled')
      if (this.isPaused()) throw new UploadPausedError()

      // Force refresh token before retry
      uploadContext.getUploadToken.invalidate?.()

      const retryTasks = failedIndices.map((i) => ({
        index: i,
        task: () => this.uploadSingleChunk(chunkList[i], i, uploadContext),
      }))

      const retryResults = await pool.execute(retryTasks)

      // Check pause/cancel after pool execution
      if (this.isCanceled()) throw new Error('canceled')
      if (this.isPaused()) throw new UploadPausedError()

      // Get updated blkWritten from retry results
      const successfulRetry = retryResults.filter((r) => r.success && r.result?.info?.blkWritten)
      if (successfulRetry.length > 0) {
        blkWritten = successfulRetry[successfulRetry.length - 1].result?.info?.blkWritten || {}
      }

      failedIndices = this.getFailedChunkIndices(blkWritten, chunkList.length)

      // If still have failed chunks, wait before next retry with exponential backoff
      if (failedIndices.length > 0) {
        const retryDelay = calculateRetryDelay(retry)
        await this.delay(retryDelay)
      }
    }
  }

  public async resumeUpload(onProgress?: (progress: UploadProgress) => void): Promise<any> {
    if (!this.pausedState) {
      throw new Error('No paused upload to resume')
    }

    this.resume()
    this.progressCallback = onProgress
    const state = this.pausedState
    this.pausedState = undefined

    if (state.isResumeUpload) {
      // Resume with chunk checking (for continue upload)
      return this.resumeUploadFromState(state, true)
    } else {
      // Resume normal upload
      return this.resumeUploadFromState(state, false)
    }
  }

  private async resumeUploadFromState(
    state: PausedUploadState,
    useResumeLogic: boolean,
  ): Promise<any> {
    try {
      if (useResumeLogic) {
        await this.uploadChunksWithResume(state.chunkList, state.uploadContext)
      } else {
        await this.uploadChunks(state.chunkList, state.uploadContext)
      }

      return await this.requestOptions.finishImagePartUploadFunc({
        imageId: state.uploadContext.imageId,
        fileMd5: state.uploadContext.md5,
      })
    } catch (error) {
      if (error instanceof UploadPausedError) {
        // Save state again if paused during resume
        this.pausedState = state
      }
      throw error
    }
  }
}

// Default image upload client instance
export const imageUploaderClient = new ImageUploaderClient({
  chunkSize: CHUNK_SIZE,
  requestOptions: {
    initImagePartUploadFunc: async ({ file, imageName, description, fileMd5, format, config }) => {
      // Register image. VM (RAW) sends format=1; Container keeps the default (0).
      // Optional config (envs/cmd/disable_ssh/min_resource) is spread in only when provided.
      const registerReq = {
        url: `${imageName}:latest`,
        name: imageName,
        description,
        size: file.size,
        checksum: fileMd5,
        image_provider: 0,
        // VM (RAW) sends format=1; Container omits it (server defaults to 0).
        ...(format !== undefined && { format }),
        ...config,
      }

      let imageId: number
      let storeServiceUrl: string

      try {
        const response = await API.registerImage(registerReq)
        if (response.code !== 0 || !response.imageID || !response.storeService) {
          throw new Error(
            `Image registration failed with code ${response.code}: ${response.msg || 'Unknown error'}`,
          )
        }
        imageId = response.imageID
        storeServiceUrl = response.storeService
      } catch (error: any) {
        // Handle already registered case (code 174)
        if (
          error.code === UPLOAD_CONSTANTS.ERROR_CODE_ALREADY_REGISTERED &&
          error.imageID &&
          error.storeService
        ) {
          imageId = error.imageID
          storeServiceUrl = error.storeService
        } else {
          throw error
        }
      }

      // Create token provider instead of fetching token once
      const getUploadToken = createImageTokenProvider(imageId)

      return {
        imageId,
        storeServiceUrl,
        getUploadToken,
      }
    },
    uploadPartImageFunc: async ({
      chunk,
      index,
      fileMd5,
      fileName,
      fileSize,
      storeServiceUrl,
      getUploadToken,
      signal,
    }) => {
      // Get fresh token for each chunk (uses 60s cache internally)
      const uploadToken = await getUploadToken()

      // Use FormData to send chunk data
      const formData = new FormData()
      formData.append('file', chunk)
      formData.append('file_name', fileName)
      formData.append('block_size', CHUNK_SIZE.toString())
      formData.append('file_size', fileSize.toString())
      formData.append('block_index', index.toString())
      formData.append('checksum', fileMd5)
      formData.append('checksum_alg', UPLOAD_CONSTANTS.CHECKSUM_ALGORITHM)

      const response = await http.post(`${storeServiceUrl}/image`, formData, {
        headers: {
          Authorization: `Bearer ${uploadToken}`,
        },
        signal,
      })

      // Check if upload was successful (code === 0 means success)
      if (response.code !== 0) {
        throw new Error(
          `Upload failed with code ${response.code}: ${response.msg || 'Unknown error'}`,
        )
      }

      // Return response in the expected UploadChunkResponse format
      return {
        info: response.info,
      }
    },
    finishImagePartUploadFunc: async ({ imageId, fileMd5 }) => {
      return { imageId, checksum: fileMd5 }
    },
  },
})
